import { Box } from '@mui/material';
import { JSX, useState } from 'react';

import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE } from 'Config/Icons';
import { DueDiligenceControlFrameworkConfiguration } from 'Models/DueDiligence';

import styles from './Modal.module.css';

export interface ControlFrameworkModalProps {
    existingNames: string[];
    hideModal: () => void;
    onSave: (framework: DueDiligenceControlFrameworkConfiguration) => void;

    /** If provided, the modal is in edit mode with this framework pre-populated. */
    framework?: DueDiligenceControlFrameworkConfiguration;
}

/**
 * Modal for adding or editing a control framework.
 * In add mode: Creates a new framework with empty control groups.
 * In edit mode: Updates the framework's name.
 */
export const ControlFrameworkModal = (props: ControlFrameworkModalProps): JSX.Element => {
    const { existingNames, framework, hideModal, onSave } = props;
    const isEditMode = !!framework;

    const [name, setName] = useState(framework?.name ?? '');
    const [errorMessage, setErrorMessage] = useState<string>();

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setName(event.target.value);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        setErrorMessage(undefined);

        const trimmedName = name.trim();

        if (trimmedName.length === 0) {
            setErrorMessage('Name is required.');
            return;
        }

        if (existingNames.includes(trimmedName) && trimmedName !== props.framework?.name) {
            setErrorMessage('A framework with this name already exists in this assessment.');
            return;
        }

        const updatedFramework: DueDiligenceControlFrameworkConfiguration = {
            control_groups: framework?.control_groups ?? [],
            name: trimmedName,
        };

        onSave(updatedFramework);
    };

    return (
        <Modal open header={isEditMode ? 'Edit Control Framework' : 'Add Control Framework'}>
            {errorMessage && <Alert type="error">{errorMessage}</Alert>}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <div className={styles.formField}>
                    <FormFieldText formFieldId="name" formFieldLabel="Name" value={name} handleChange={handleNameChange} required />
                </div>

                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Cancel
                    </Button>
                    <Button variant="submit">Save</Button>
                </div>
            </Box>
        </Modal>
    );
};
